import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { PositionDescription, getJobDescriptionData } from '../onboarding/onboarding.service';

interface JobRoleContextType {
  jobRoleData: PositionDescription | null;
  loading: boolean;
  error: string | null;
}

const JobRoleContext = createContext<JobRoleContextType | undefined>(undefined);

interface JobRoleProviderProps {
  children: ReactNode;
}

export const JobRoleProvider = (props: JobRoleProviderProps) => {
  const { children } = props;
  const { jobRoleId: urlJobRoleId } = useParams();
  const [jobRoleData, setJobRoleData] = useState<PositionDescription | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get jobRoleId from URL params or sessionStorage
  const getJobRoleId = () => {
    if (urlJobRoleId) {
      return urlJobRoleId;
    }
    return sessionStorage.getItem('currentJobRoleId');
  };

  const jobRoleId = getJobRoleId();

  useEffect(() => {
    const fetchJobRoleData = async () => {
      if (jobRoleId) {
        sessionStorage.setItem('currentJobRoleId', jobRoleId);

        setLoading(true);
        setError(null);
        try {
          const data = await getJobDescriptionData(jobRoleId);
          setJobRoleData(data);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch job role data');
        } finally {
          setLoading(false);
        }
      } else {
        setJobRoleData(null);
        setLoading(false);
        setError(null);
      }
    };

    fetchJobRoleData();
  }, [jobRoleId]);

  const value: JobRoleContextType = {
    jobRoleData,
    loading,
    error,
  };

  return (
    <JobRoleContext.Provider value={value}>
      {children}
    </JobRoleContext.Provider>
  );
};

export const useJobRole = (): JobRoleContextType => {
  const context = useContext(JobRoleContext);
  if (context === undefined) {
    throw new Error('useJobRole must be used within a JobRoleProvider');
  }
  return context;
};
